// DeveloperAuthContext.jsx - Supabase authentication context for developer portal
import React, { createContext, useContext, useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

let supabase = null;
if (supabaseUrl && supabaseAnonKey) {
  supabase = createClient(supabaseUrl, supabaseAnonKey);
}

const DeveloperAuthContext = createContext(null);

export function useDeveloperAuth() {
  const context = useContext(DeveloperAuthContext);
  if (!context) {
    throw new Error('useDeveloperAuth must be used within a DeveloperAuthProvider');
  }
  return context;
}

export function DeveloperAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if Supabase is configured
  const isConfigured = !!supabase;

  // Initialize auth state
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.access_token);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          await loadProfile(session.access_token);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Load developer profile from backend
  const loadProfile = async (token) => {
    try {
      const response = await fetch('/api/developer/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUp = async (email, password) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured' } };
    }

    setError(null);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/developers`
      }
    });

    if (error) {
      setError(error.message);
      return { error };
    }

    return { data };
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured' } };
    }

    setError(null);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      setError(error.message);
      return { error };
    }

    return { data };
  };

  // Sign out
  const signOut = async () => {
    if (!supabase) return;

    setError(null);
    const { error } = await supabase.auth.signOut();

    if (error) {
      setError(error.message);
    }

    setUser(null);
    setSession(null);
    setProfile(null);
  };

  // Reset password
  const resetPassword = async (email) => {
    if (!supabase) {
      return { error: { message: 'Authentication not configured' } };
    }

    setError(null);
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/developers/reset-password`
    });

    if (error) {
      setError(error.message);
      return { error };
    }

    return { data };
  };

  // Update profile
  const updateProfile = async (name, organization) => {
    if (!session?.access_token) {
      return { error: { message: 'Not authenticated' } };
    }

    setError(null);
    try {
      const response = await fetch('/api/developer/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, organization })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to update profile');
        return { error: data };
      }

      setProfile(data.profile);
      return { data };
    } catch (err) {
      setError(err.message);
      return { error: { message: err.message } };
    }
  };

  // Get auth token for API calls
  const getToken = async () => {
    if (!session?.access_token) return null;
    return session.access_token;
  };

  // Make authenticated API call
  const authFetch = async (url, options = {}) => {
    const token = await getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  const value = {
    user,
    profile,
    session,
    loading,
    error,
    isConfigured,
    isAuthenticated: !!user,
    needsProfile: !!user && !profile,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    getToken,
    authFetch,
    loadProfile: () => session?.access_token && loadProfile(session.access_token)
  };

  return (
    <DeveloperAuthContext.Provider value={value}>
      {children}
    </DeveloperAuthContext.Provider>
  );
}

export default DeveloperAuthContext;
